import { IonContent, IonList } from '@ionic/react';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CONVERSATION_STATUS } from '../../constants';
import {
  ConversationContext,
  PaymentContext,
  TranslationContext,
  UserContext,
} from '../../contexts';
import { analyticsHelper, conversationHelper } from '../../helpers';
import { webAnalyticsHelper } from '../../helpers/webAnalytics';
import { AuthorType, Expert, Message, MessageType, User } from '../../models';
import { conversationResource } from '../../resource';
import './chat.scss';
import { ChatInput } from './ChatInput';
import { ChatItem } from './ChatItem';
import { ChatSystemItem } from './ChatSystemItem';

interface Props {
  discussion: Message[];
  send: (
    conversation: string,
    message: string,
    type?: MessageType,
    author?: string,
    authorIdentifier?: string,
    authorType?: string,
    conversationBetween?: object,
  ) => void;
}

export const Chat: React.FC<Props> = ({ discussion, send }) => {
  const {
    idConversation,
    readOnly,
    target,
    status,
    setStatus,
    setConversation,
    conversation,
    isExpertTyping,
    setIsExpertTyping,
  } = useContext(ConversationContext);
  const { translate } = useContext(TranslationContext);
  const { isCustomer, isExpert, user: currentUser } = useContext(UserContext);
  const user = currentUser as User;

  const {
    toppedUpBalance,
    credits,
    minutesToUse,
    refreshCredits,
    resetToppedUp,
  } = useContext(PaymentContext);
  const inputMessage = useRef<HTMLIonTextareaElement>(null);
  const chatContent = useRef<HTMLIonContentElement>(null);
  const [addCreditButtonVisible, setAddCreditButtonVisible] =
    useState<boolean>(false);
  const [isWelcomeMessage, setIsWelcomeMessage] = useState<boolean>(false);
  const [isResumeMessage, setIsResumeMessage] = useState<boolean>(false);
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
  const [showNetworkIssue, setShowNetworkIssue] = useState<boolean>(false);
  const expertTypingTimestampRef = useRef<number | null>(null);
  const expert = target as Expert;

  const handleSend = useCallback(
    (text: string) => {
      if (!readOnly) {
        setIsSendingMessage(true);
        send(
          idConversation,
          text,
          MessageType.MESSAGE,
          user.name,
          user.authorIdentifier,
          isCustomer ? AuthorType.CUSTOMER : AuthorType.EXPERT,
          [user.authorIdentifier, expert.authorIdentifier],
        );
      }
    },
    [
      readOnly,
      send,
      idConversation,
      user.name,
      user.authorIdentifier,
      isCustomer,
      expert.authorIdentifier,
    ],
  );
  // When customer click on resume conversation
  const onResume = () => {
    setIsResumeMessage(true);
    const resumeConversation = async () => {
      setConversation(await conversationResource.resume(idConversation));
    };
    const justJoinedMessages = [MessageType.JOIN, MessageType.CREDIT_ADDED];
    const targetLastJoinIndex = conversationHelper.getLastOfType(
      discussion,
      justJoinedMessages,
      expert.name,
    );
    const targetLastLeaveIndex = conversationHelper.getLastOfType(
      discussion,
      [MessageType.LEAVE],
      expert.name,
    );
    const isExpertJoinedConversation =
      targetLastJoinIndex > targetLastLeaveIndex;

    if (isExpertJoinedConversation) {
      // Both are connected
      resumeConversation();
      setIsResumeMessage(false);
    } else {
      // Expert not there, hide resume button and notify expert that customer joined the chat
      const doSend = async () => {
        resumeConversation().then();
        // Mey be this notification will have to be removed
        await send(
          idConversation,
          translate('chat.resume', { name: user.name }),
          MessageType.RESUME,
          user.name,
          user.authorIdentifier,
          AuthorType.SYSTEM,
          [user.authorIdentifier, expert.authorIdentifier],
        );
        analyticsHelper.logEvent('chat_resume', {
          UserName: user.name,
          Target: target!.name,
          Credits: credits,
        });
        webAnalyticsHelper.logEvent('chat_resume', {
          UserName: user.name,
          Target: target!.name,
          Credits: credits,
        });
        setStatus(CONVERSATION_STATUS.ACTIVE);
      };

      doSend().then();
    }
  };

  const isActive = useMemo(
    () =>
      [
        CONVERSATION_STATUS.ACTIVE,
        CONVERSATION_STATUS.CREDIT_ADDED,
        CONVERSATION_STATUS.START,
        CONVERSATION_STATUS.CREDIT_PENDING,
        CONVERSATION_STATUS.RESUME,
      ].includes(status),
    [status],
  );
  const isPending = useMemo(
    () => status === CONVERSATION_STATUS.CREDIT_PENDING,
    [status],
  );
  const isStopped = useMemo(
    () => status === CONVERSATION_STATUS.STOP,
    [status],
  );
  const isResumed = useMemo(
    () => status === CONVERSATION_STATUS.RESUME,
    [status],
  );

  /**
   * Sends a "join conversation" message to Mercure when:
   * - component is mount in write mode
   * - readonly becomes true
   * - conversation is not stopped (waiting for resume)
   */
  useEffect(() => {
    const doSend = async () => {
      await send(
        idConversation,
        translate('chat.join', { name: user.name }),
        MessageType.JOIN,
        user.name,
        user.authorIdentifier,
        AuthorType.SYSTEM,
        [user.authorIdentifier, expert.authorIdentifier],
      );
      analyticsHelper.logEvent('chat_start', {
        UserName: user.name,
        Target: target!.name,
        Credits: credits,
      });
      webAnalyticsHelper.logEvent('chat_start', {
        UserName: user.name,
        Target: target!.name,
        Credits: credits,
      });
    };

    // Don't send JOIN message if conversation is stopped (waiting for resume)
    if (!readOnly && !isStopped) {
      doSend();
    }

    // eslint-disable-next-line
  }, [readOnly]);

  /**
   * Sends a "credit added" message to Mercure and resume conversation when:
   * - Customer just refilled is wallet
   */
  useEffect(() => {
    if (idConversation && isCustomer && toppedUpBalance) {
      send(
        idConversation,
        translate('chat.availableCredit'),
        MessageType.CREDIT_ADDED,
        user.name,
      );
      const resumeConversation = async () =>
        setConversation(await conversationResource.resume(idConversation));
      resumeConversation();
      resetToppedUp();
      analyticsHelper.logEvent('chat_resume_credit_added', {
        UserName: user.name,
        Target: target!.name,
        Credits: credits,
      });
      webAnalyticsHelper.logEvent('chat_resume_credit_added', {
        UserName: user.name,
        Target: target!.name,
        Credits: credits,
      });
    }
    // eslint-disable-next-line
  }, [
    idConversation,
    isCustomer,
    send,
    translate,
    user,
    toppedUpBalance,
    resetToppedUp,
  ]);

  /**
   * Try to guess the current status of the conversation to update the state and display/hide the "add credit" button when:
   * - the list of messages has been updated
   *
   * description:
   * Starting from the most recent message, we're looking for "non-active" message.
   * "start", "credit_added", "credit_pending", "stop", "resume_credit_added" are consider as "non-active message.
   * When one of this king of message is found, we stop the loop and update the status of the conversation.
   */
  useEffect(() => {
    // Don't override status from context if there are no messages
    if (discussion.length === 0) {
      return;
    }

    let newStatus = null; // Don't default to ACTIVE, let it be null if no status message found
    let i = discussion.length - 1;
    while (newStatus === null && i >= 0) {
      const currentMessage = discussion[i];

      switch (currentMessage.type) {
        case MessageType.START:
          newStatus = CONVERSATION_STATUS.START;
          setAddCreditButtonVisible(false);
          break;
        case MessageType.CREDIT_ADDED:
          newStatus = CONVERSATION_STATUS.CREDIT_ADDED;
          setAddCreditButtonVisible(false);
          break;
        case MessageType.CREDIT_PENDING:
          newStatus = CONVERSATION_STATUS.CREDIT_PENDING;
          setAddCreditButtonVisible(true); // Set this flag to tell to keep the "add credits" button
          break;
        case MessageType.STOP:
          newStatus = CONVERSATION_STATUS.STOP;
          setAddCreditButtonVisible(false); // Set this flag to tell to hide the "add credits" button
          break;
        case MessageType.RESUME || MessageType.RESUME_CREDIT_ADDED:
          // Don't override STOP status with RESUME - STOP from backend takes priority
          if (status !== CONVERSATION_STATUS.STOP) {
            newStatus = CONVERSATION_STATUS.RESUME;
          }
          setAddCreditButtonVisible(false);
          break;
      }

      i--;
    }

    // Only update status if we found a status-related message
    if (newStatus !== null) {
      setStatus(newStatus);
    }
    // eslint-disable-next-line
  }, [discussion]);

  /**
   * Sends a "leave conversation" message to Mercure when:
   * - the component is unmount and only for expert in write mode
   *
   * (when expert switch between conversations, the component is not unmount, only data change)
   * (the "leave" message for the customer is handled when customer hits the back button)
   */
  useEffect(() => {
    return () => {
      // Remark: this suppose that readOnly is only for past conversation on the expert side
      if (!readOnly && isExpert) {
        send(
          idConversation,
          translate('chat.leave', { name: user.name }),
          MessageType.LEAVE,
          user.name,
          user.authorIdentifier,
          AuthorType.SYSTEM,
          [user.authorIdentifier, expert.authorIdentifier],
        );
        analyticsHelper.logEvent('chat_leave', {
          UserName: user.name,
          Target: target!.name,
          Credits: credits,
        });
        webAnalyticsHelper.logEvent('chat_leave', {
          UserName: user.name,
          Target: target!.name,
          Credits: credits,
        });
      }
    };
    // eslint-disable-next-line
  }, []);

  /**
   * Sends a "start conversation/timer" message to Mercure when:
   * - the user is the second user to join since the last stop
   * - a customer just add credit
   * - a customer has click on "resume conversation" button
   */
  useEffect(() => {
    if (!readOnly && discussion.length && user && target) {
      const last = discussion[discussion.length - 1];
      const justJoinedMessages = [
        MessageType.CREDIT_ADDED,
        MessageType.RESUME,
        MessageType.RESUME_CREDIT_ADDED,
      ];
      const justJoined =
        justJoinedMessages.includes(last.type) &&
        (last.author === undefined || last.author === user.name);
      const targetLastJoinIndex = conversationHelper.getLastOfType(
        discussion,
        justJoinedMessages,
        target.name,
      );
      const targetLastLeaveIndex = conversationHelper.getLastOfType(
        discussion,
        [MessageType.LEAVE],
        target.name,
      );
      const lastStartIndex = conversationHelper.getLastOfType(discussion, [
        MessageType.START,
      ]);

      setIsWelcomeMessage(lastStartIndex === -1);

      if (!justJoined || targetLastJoinIndex === -1) {
        setIsResumeMessage(false);
        return;
      }

      const startConversation = async () =>
        setConversation(await conversationResource.start(idConversation));
      if (
        targetLastJoinIndex > targetLastLeaveIndex &&
        status !== CONVERSATION_STATUS.START
      ) {
        startConversation().then();
        setIsResumeMessage(false);
      }
    }
    // eslint-disable-next-line
  }, [discussion, idConversation, user, target, setConversation]);

  /**
   * Scrolls to the bottom when a new message arrives
   * (there's a small delay before scroll to prevent bug when a lots of messages arrive at once - on mount with a
   * big history for example)
   */
  useEffect(() => {
    // we need to set a small delay to have the full size of the render before scrolling
    setTimeout(() => {
      if (
        chatContent &&
        chatContent.current &&
        chatContent.current.scrollToBottom
      ) {
        chatContent.current.scrollToBottom(100);
      }
    }, 800);

    if (inputMessage && inputMessage.current) {
      inputMessage.current.focus();
    }
  }, [discussion]);

  /**
   * Refresh credit on "stop" or "pending" statuses
   */
  useEffect(() => {
    if (isCustomer && (isStopped || isPending)) {
      refreshCredits();
      analyticsHelper.logEvent(isStopped ? 'chat_stopped' : 'chat_paused', {
        UserName: user.name,
        Target: target!.name,
        Credits: credits,
      });
      webAnalyticsHelper.logEvent(isStopped ? 'chat_stopped' : 'chat_paused', {
        UserName: user.name,
        Target: target!.name,
        Credits: credits,
      });
    }

    // eslint-disable-next-line
  }, [isCustomer, isStopped, isPending]);

  // Track when expert typing starts and stops
  useEffect(() => {
    if (!isExpertTyping) {
      setIsSendingMessage(false);
      stopPingRetry();
    }
  }, [isExpertTyping]);

  // Auto-reset expert typing after x seconds timeout and ping retry mechanism
  const typingTimeoutRef = useRef<number | null>(null);
  const pingRetryIntervalRef = useRef<number | null>(null);
  const lastPingTimestampRef = useRef<number | null>(null);

  const sendPing = async () => {
    const timestamp = Date.now();
    lastPingTimestampRef.current = timestamp;
    // ping is set at timestamp

    await send(
      idConversation,
      `${user.name} is waiting for pong  - ${timestamp}`,
      MessageType.PING,
      user.name,
      user.authorIdentifier,
      AuthorType.SYSTEM,
    );
  };

  const startPingRetry = () => {
    if (pingRetryIntervalRef.current) {
      clearInterval(pingRetryIntervalRef.current);
    }

    sendPing();

    pingRetryIntervalRef.current = window.setInterval(() => {
      sendPing();
    }, 30000);
  };

  const stopPingRetry = () => {
    if (pingRetryIntervalRef.current) {
      clearInterval(pingRetryIntervalRef.current);
      pingRetryIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isSendingMessage && typeof setIsSendingMessage === 'function') {
      // timer initialization

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        // Clean up the previous timer if it exists
      }

      typingTimeoutRef.current = window.setTimeout(() => {
        startPingRetry();
        typingTimeoutRef.current = null;
      }, 45000);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      stopPingRetry();
    };
  }, [isSendingMessage, setIsSendingMessage, send, idConversation, user]);

  // Handle PING/PONG messages for network issue display and stop ping retry on PONG
  useEffect(() => {
    if (discussion.length > 0) {
      const lastMessage = discussion[discussion.length - 1];

      if (lastMessage.type === MessageType.PING) {
        setShowNetworkIssue(true);
      } else if (lastMessage.type === MessageType.PONG) {
        const now = Date.now();
        const lastPing = lastPingTimestampRef.current;

        if (!lastPing || now - lastPing > 15000) {
          // pong must match the last ping timestamp or be within 15 seconds to be valid
          return;
        }

        setShowNetworkIssue(false);
        stopPingRetry();

        // Stop expert typing when pong is received (connection restored)
        // Needed to prevent the unjam chat on anthropic error - may have side effects. If user does not receive the
        // expert finish typing mesage, but the pong, it won't unjam...
        // if (setIsExpertTyping) {
        //   setIsExpertTyping(false);
        // }
      }
    }
  }, [discussion, setIsExpertTyping]);

  return (
    <Fragment>
      <IonContent
        className="chat-content"
        id="chat-content"
        data-testid="chat-content"
        ref={chatContent}
      >
        <IonList lines="none">
          {isCustomer && isWelcomeMessage ? (
            <ChatSystemItem
              message={translate('chat.welcomeMessage', {
                name: target ? target.name : '',
              })}
              centered={true}
            />
          ) : null}
          {discussion.map((item, index) => (
            <ChatItem
              isLast={index === discussion.length - 1}
              item={item}
              previousItem={index > 0 ? discussion[index - 1] : undefined}
              key={index}
            />
          ))}
        </IonList>

        {isCustomer &&
        (addCreditButtonVisible || isPending) &&
        !minutesToUse &&
        credits < (conversation ? conversation.minuteRate : 0) ? (
          <ChatSystemItem
            // old chat.creditPendingCustomer message
            showWallet
          />
        ) : null}
        {isCustomer &&
        (addCreditButtonVisible || isPending) &&
        (minutesToUse ||
          credits >= (conversation ? conversation.minuteRate : 0)) ? (
          <ChatSystemItem showResume onResume={onResume} />
        ) : null}
        {isCustomer && isStopped ? (
          !minutesToUse &&
          credits < (conversation ? conversation.minuteRate : 0) ? (
            // Old chat.topUp message
            <ChatSystemItem showWallet />
          ) : minutesToUse ||
            credits >= (conversation ? conversation.minuteRate : 0) ? (
            <ChatSystemItem showResume onResume={onResume} />
          ) : null
        ) : null}
        {isCustomer && isResumeMessage ? (
          <ChatSystemItem
            message={translate('chat.welcomeMessage', {
              name: target ? target.name : '',
            })}
            centered={true}
          />
        ) : null}
        {isCustomer && isExpertTyping && !showNetworkIssue ? (
          <ChatSystemItem
            message={translate('chat.messageExpertTyping', {
              expert: target ? target.name : '',
            })}
            centered={true}
          />
        ) : null}
        {showNetworkIssue ? (
          <ChatSystemItem
            message={translate('chat.networkIssue')}
            centered={true}
          />
        ) : null}
      </IonContent>

      <ChatInput
        disabled={readOnly || !isActive}
        sendDisabled={
          readOnly ||
          !isActive ||
          (isCustomer && (isExpertTyping || isSendingMessage))
        }
        isExpertTyping={isExpertTyping}
        isSendingMessage={isSendingMessage}
        onSend={handleSend}
        textareaRef={inputMessage}
      />
    </Fragment>
  );
};
